import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  Alert,
  Card,
  Checkbox,
  Col,
  Empty,
  Popover,
  Row,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  DownloadOutlined,
  EditOutlined,
  FileTextTwoTone,
  MailOutlined,
} from '@ant-design/icons';
import {
  FormattedDate,
  FormattedDateParts,
  FormattedTime,
  useIntl,
} from 'react-intl';
import ClassNames from 'classnames';
import {
  IAppointment,
  ICurrentFormField,
  IUserInfo,
  IWorkTypeAnswers,
  IWorkTypeData,
} from '../../../constants/types';
import styles from './contentWorkType.module.scss';
import QmIcon from '../../../components/icons/qm';
import TechnicianIcon from '../../../components/icons/user';
import { Image } from 'antd';
import translation from '../../../i18n/translation';
import Comments from '../../../components/comments/comments';
import { useDispatch } from 'react-redux';
import WorkTypeAction from '../actions';
import { findStatusTagLabel, getStatusColor } from '../../../utils/helpers';
import CarouselSlider from '../../../components/carouselSlider/carouselSlider';
import ImageEdit from '../../../components/imageSelector/imageEdit';
import { IFeatures } from '../../../store/rolesAndPrivileges';
import * as routes from '../../../router/routes';
import MandatoryImageSelection from '../mandatoryImageSelection';
import { showNotificationMessage } from '../../../utils/notification';
import VideoPlayer from '../../../components/video';
import { store } from '../../../store';
import { getEnv } from '../../../utils/environment';
import { Roles } from 'craftos-ui';
import { UKFlagSvg } from '../../../components/icons/ukFlagSvg';

interface SelectedCheckbox {
  id: string;
  label: Array<string>;
}

interface ContentWorkTypeProps {
  workTypeData: IWorkTypeData;
  currentFormField: ICurrentFormField;
  selectedCheckbox?: SelectedCheckbox[];
  userData: IAppointment;
  formRevisions?: any;
  fieldId: string;
  page: string;
  appointmentId: string;
  handleGoToNextStep: (arg0: number | undefined) => void;
  showSteps: boolean;
  numberOfSteps: number;
  setNumberOfSteps: Dispatch<SetStateAction<2 | 3>>;
  images: Array<string>;
  currentStep: number;
}

const ContentWorkType = ({
  workTypeData = {},
  selectedCheckbox = [],
  currentFormField,
  formRevisions,
  userData,
  fieldId,
  page,
  appointmentId,
  handleGoToNextStep,
  showSteps,
  numberOfSteps,
  setNumberOfSteps,
  images,
  currentStep,
}: ContentWorkTypeProps) => {
  const { Paragraph, Text, Link } = Typography;
  const intl = useIntl();
  const dispatch = useDispatch();
  const { loginUser, privileges } = store.getState().authentication;

  let roles = loginUser!.testRoles;
  if (getEnv() === 'production') {
    roles = loginUser.roles;
  }

  const hasApprovalRights =
    privileges![IFeatures.SuperAdminAccess] ||
    (!formRevisions?.allowedRolesForApproval?.length &&
      privileges![IFeatures.fieldDetailApproval]) ||
    roles!.some((role: Roles) =>
      formRevisions?.allowedRolesForApproval?.includes(role)
    );

  const [previewImageIndex, setPreviewImageIndex] = useState<any>(null);
  const [editImageIndex, setEditImageIndex] = useState<any>(null);
  const [commentImageList, setCommentImageList] = useState<string[]>([]);
  const [imageSubmitSuccess, setImageSubmitSuccess] = useState<
    boolean | undefined
  >(undefined);
  const [error, setError] = useState<string>();

  const note = workTypeData?.note;
  const title = currentFormField.formField?.label;
  const teamName = userData?.team?.name;

  const getAuthorDetails = (answer: IWorkTypeAnswers) => {
    let author = answer.reopened
      ? answer.reopenedBy ?? {}
      : answer.createdBy ?? {};
    return {
      ...author,
      name: author.name
        ? author.name
        : intl.formatMessage({
            id:
              answer.actor === 'User' && !answer.reopened
                ? 'submission'
                : 'quality_manager',
          }),
    };
  };

  useEffect(() => {
    if (fieldId) {
      setImageSubmitSuccess(undefined);
      setNumberOfSteps(3);
      setError(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldId]);

  useEffect(() => {
    if (error) {
      showNotificationMessage({
        message: intl.formatMessage({
          id: 'error_upload_images',
        }),
        error: error,
      });
      handleGoToNextStep(3);
      setNumberOfSteps(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const renderAuthorDetails = (author: IUserInfo) => {
    let email = author.username || author.email;
    return email ? (
      <div>
        <Text>{author.name}</Text>
        <div>
          <Space>
            <Text type="secondary">
              <MailOutlined />
            </Text>
            <Link href={`mailto:${email}`}>{email}</Link>
          </Space>
        </div>
      </div>
    ) : null;
  };

  const addImageToCommentImageList = (tempBlob: any) => {
    //tempBlob example: "http://localhost:3000/1eead189-3f86-4a06-8044-432ca38c4371"
    setCommentImageList([...commentImageList, tempBlob]);
  };
  const removeImagefromCommentImageList = (url: any) => {
    //tempBlob example: "http://localhost:3000/1eead189-3f86-4a06-8044-432ca38c4371"
    setCommentImageList(commentImageList.filter((item) => item !== url));
  };
  const renderLanguageFlag = (language: any) => {
    if (!language) return null;
    let flagSvg = null;
    switch (language) {
      case 'de':
        return null;
      default:
        flagSvg = (
          <Text className={styles.ukFlag}>
            <UKFlagSvg />
          </Text>
        );
    }
    return <div className={styles.centeredContentContainer}>{flagSvg}</div>;
  };

  useEffect(() => {
    if (workTypeData?.answers && title && showSteps) {
      if (imageSubmitSuccess === undefined) {
        workTypeData?.answers[0]?.status === 'Approved' ||
        workTypeData?.answers[0]?.status === 'Rejected'
          ? handleGoToNextStep(3)
          : handleGoToNextStep(1);
      }
    }

    if (imageSubmitSuccess) {
      handleGoToNextStep(3);
      // the last images are not displayed if we don't refetch:
      dispatch(WorkTypeAction.getFreshAnswerData(fieldId, ''));
      setImageSubmitSuccess(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSubmitSuccess, showSteps, workTypeData?.answers, title]);

  return (
    <div
      className={ClassNames(styles.wrapper, {
        [styles.notTitle]: !Boolean(title),
      })}
    >
      {note && note.text && (
        <Alert
          message={<Text>{note.text}</Text>}
          showIcon
          type="warning"
          className={styles.noteContentBox}
          icon={
            <Row align="middle" style={{ marginRight: '40px' }}>
              <Col>
                <span style={{ marginRight: '6px' }}>
                  <FileTextTwoTone
                    twoToneColor="#ffe58f"
                    style={{ fontSize: '32px' }}
                  />
                </span>
              </Col>
              <Col>
                <Text strong>{teamName}</Text>
                <br />
                <Text type="secondary">
                  {intl.formatDate(note.updatedTime)}
                </Text>
              </Col>
            </Row>
          }
        />
      )}

      <div className={styles.content}>
        {workTypeData?.answers && workTypeData?.answers?.length ? (
          showSteps &&
          numberOfSteps === 3 &&
          currentStep === 2 &&
          images?.length > 0 ? (
            <MandatoryImageSelection
              images={images}
              appointmentId={appointmentId}
              formFieldId={workTypeData?.formFieldId}
              formInstanceId={workTypeData?.formInstance?.id}
              externalCaseId={userData?.externalCaseId}
              setImageSubmitSuccess={setImageSubmitSuccess}
              setError={setError}
            />
          ) : (
            workTypeData?.answers.map(
              (item: IWorkTypeAnswers, index: number): React.ReactNode => {
                const author = getAuthorDetails(item);
                const commentList = item.comments;
                return (
                  <div
                    className={styles.itemContent}
                    key={`itemContent-${index}`}
                  >
                    <Row align="middle" className={styles.dividerText}>
                      <Col>
                        <Popover
                          content={renderAuthorDetails(author)}
                          placement="bottom"
                        >
                          <span style={{ cursor: 'pointer' }}>
                            {item.actor === 'User' && !item.reopened ? (
                              <TechnicianIcon />
                            ) : (
                              <QmIcon />
                            )}
                          </span>
                        </Popover>
                      </Col>
                      <Col>
                        <Space>
                          <Text strong>{author.name}</Text>
                          {item?.actor === 'User' &&
                            !item?.reopened &&
                            renderLanguageFlag(item?.preferredLanguage)}
                        </Space>
                        <br />
                        <Text type="secondary">
                          <FormattedDate value={item.createdAt} /> -{' '}
                          <FormattedTime value={item.createdAt} />
                        </Text>
                      </Col>
                      <Col className={styles.answerStatusTag}>
                        {item.status && (
                          <Tag
                            style={{
                              background: getStatusColor(item.status, true),
                            }}
                            className={styles.status}
                          >
                            {!currentFormField.formField?.approvalRequired &&
                            item.status === 'Approved'
                              ? 'Auto-Approved'
                              : findStatusTagLabel(item.status ?? '')}
                          </Tag>
                        )}
                      </Col>
                    </Row>

                    <div className={styles.answersContent}>
                      {item.selections && (
                        <div className={styles.checkboxGroup}>
                          {selectedCheckbox?.length &&
                            selectedCheckbox.map(
                              (val: SelectedCheckbox, index: number) => {
                                return (
                                  <Checkbox
                                    className={styles.checkbox}
                                    key={`${val.id}-${index}`}
                                    value={val.id}
                                    checked={
                                      item.selections
                                        ? item.selections.includes(val.id)
                                        : false
                                    }
                                  >
                                    {val.label}
                                  </Checkbox>
                                );
                              }
                            )}
                        </div>
                      )}

                      {item.content && <Paragraph>{item.content}</Paragraph>}

                      {item.uploads ? (
                        <div>
                          <Card bordered={false}>
                            {item.uploads.map((val: string, i: number) => (
                              <Card.Grid
                                key={`img-card-${item.id}-${i}`}
                                className={styles.imageCard}
                                style={{
                                  position: 'relative',
                                }}
                              >
                                <Image
                                  src={val}
                                  preview={false}
                                  style={{ maxHeight: '320px' }}
                                  fallback="data:image/png;base64,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"
                                />
                                {(((workTypeData.status === 'Rejected' ||
                                  workTypeData.status === 'Approved') &&
                                  privileges![IFeatures.fieldDetailComment]) ||
                                  (workTypeData.status !== 'Rejected' &&
                                    workTypeData.status !== 'Approved' &&
                                    hasApprovalRights)) && (
                                  <div
                                    className={styles.editIconContainer}
                                    onClick={() =>
                                      setEditImageIndex(
                                        item.id ? { [item.id]: i } : null
                                      )
                                    }
                                  >
                                    <EditOutlined
                                      style={{
                                        opacity: '0.9',
                                        fontSize: '14px',
                                      }}
                                    />
                                  </div>
                                )}
                                <a
                                  className={styles.downloadIconContainer}
                                  target="_blank"
                                  href={val}
                                  rel="noreferrer"
                                >
                                  <DownloadOutlined
                                    style={{
                                      opacity: '0.9',
                                      fontSize: '14px',
                                    }}
                                  />
                                </a>
                                <div
                                  className={styles.hoverableOverlay}
                                  onClick={() =>
                                    setPreviewImageIndex(
                                      item.id ? { [item.id]: i } : null
                                    )
                                  }
                                ></div>
                              </Card.Grid>
                            ))}
                          </Card>
                          <div style={{ display: 'none' }}>
                            {editImageIndex ? (
                              <ImageEdit
                                key={`editor-image-${editImageIndex}`}
                                src={
                                  item.uploads[
                                    editImageIndex[item.id ?? '']
                                      ? editImageIndex[item.id ?? '']
                                      : 0
                                  ]
                                }
                                okText={intl.formatMessage({
                                  id: 'attach_to_comment',
                                })}
                                isDisabled={
                                  !!commentImageList.length &&
                                  commentImageList.length >= 5
                                }
                                isVisible={
                                  !!editImageIndex &&
                                  editImageIndex[item.id ?? ''] > -1
                                }
                                onSelect={addImageToCommentImageList}
                                onClose={() => setEditImageIndex(null)}
                              />
                            ) : null}
                            {previewImageIndex ? (
                              <CarouselSlider
                                imageUrls={item.uploads}
                                currentIndex={
                                  previewImageIndex &&
                                  previewImageIndex[item.id ?? '']
                                    ? previewImageIndex[item.id ?? '']
                                    : 0
                                }
                                visible={
                                  !!previewImageIndex &&
                                  previewImageIndex[item.id ?? ''] > -1
                                }
                                onVisibleChange={() =>
                                  setPreviewImageIndex(null)
                                }
                              />
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                      {item?.videoUploads ? (
                        <VideoPlayer url={item.videoUploads[0]} />
                      ) : null}

                      {item.dateContent && (
                        <Paragraph>
                          <FormattedDateParts
                            value={item.dateContent}
                            weekday="short"
                            year="numeric"
                            day="2-digit"
                            month="long"
                          >
                            {(parts) => (
                              <>
                                {parts[0].value}., {parts[4].value}{' '}
                                {parts[2].value} {parts[3].value}{' '}
                                {parts[6].value},{' '}
                              </>
                            )}
                          </FormattedDateParts>
                          <FormattedTime value={item.dateContent} />
                        </Paragraph>
                      )}
                    </div>
                    {currentFormField.formField?.approvalRequired && (
                      <Comments
                        comments={commentList}
                        isActiveSubmission={index === 0}
                        answerSnapshot={
                          workTypeData?.answers &&
                          workTypeData?.answers.length > 0
                            ? workTypeData?.answers[0]
                            : undefined
                        }
                        hasApprovalRights={hasApprovalRights}
                        disableActions={
                          ['Open', 'Rejected', 'Approved'].includes(
                            workTypeData.status ?? ''
                          ) || userData?.status === 'Closed'
                        }
                        fieldStatus={workTypeData.status ?? ''}
                        commentImageList={commentImageList}
                        onSetCommentImageList={setCommentImageList}
                        onRemoveImageFromList={removeImagefromCommentImageList}
                        sendComment={dispatch(WorkTypeAction.sendComment)}
                        handleGoToNextStep={handleGoToNextStep}
                        setImageSubmitSuccess={setImageSubmitSuccess}
                        setNumberOfSteps={setNumberOfSteps}
                        showSteps={showSteps}
                        fieldId={fieldId}
                        approvalType={currentFormField.formField?.approvalType}
                        clipboardData={{
                          customerName: userData?.customer?.name ?? '',
                          customerAddress:
                            userData?.customerAddress?.address1 ?? '',
                          workType: currentFormField.workType ?? '',
                          field: currentFormField.formField?.label,
                          appointmentPage: `${
                            window.location.origin
                          }${routes.appointmentsDetails.pathWithParams(
                            page,
                            appointmentId
                          )}`,
                          workTypePage: `${
                            window.location.origin
                          }${routes.workType.pathWithParams(
                            page,
                            appointmentId,
                            fieldId
                          )}`,
                        }}
                        appointmentId={appointmentId}
                        appointment={{ ...userData }}
                        questions={workTypeData.questions}
                      />
                    )}
                  </div>
                );
              }
            )
          )
        ) : (
          <Empty
            description={
              <Text type="secondary">{translation('no_submissions')}</Text>
            }
            className={styles.emptySubmissions}
          />
        )}
      </div>
    </div>
  );
};

export default ContentWorkType;
